<template>
  <div class="app">
    <transition name="fade" mode="out-in">
      <template v-if="$store.state.loading">
        <component-loader :message="message" />
      </template>

      <template v-else>
        <div :class="$route.name" data-cy="app-content" :data-cy-info="`app-content-${$route.name}`">
          <component-topbar v-if="layout.topbar" />
          <component-environment-banner />

          <div id="content" ref="content" class="content" :class="{ 'split': split.visible, 'split-reverse': layout.split.reverse }">
            <div ref="page" class="page" :style="styles">
              <transition name="fade" mode="out-in" @before-enter="beforeEnter">
                <router-view />
              </transition>
            </div>

            <div v-if="split.visible" ref="flex" class="flex">
              <transition name="fade" mode="out-in" @before-enter="beforeEnter">
                <template v-if="split.flex">
                  <iframe class="flex-frame" :src="$globals.flex.url" allow="microphone" frameborder="0" />
                </template>

                <template v-else>
                  <div class="flex-prompt">
                    Call centre is currently disabled.
                    <div style="margin-top: 1em;">
                      <button class="uk-button uk-button-primary uk-button-small" style="margin-right: 1em;" @click="split.flex = true">Enable</button>
                      <button class="uk-button uk-button-secondary uk-button-small" @click="split.enabled = false">Hide</button>
                    </div>
                  </div>
                </template>
              </transition>

              <div class="controls">
                <div class="button" @click="adjustSplit('resize', 'down')"><div class="icon gg-push-left" style="margin-left: 4px;" /></div>
                <div class="button" @click="adjustSplit('swap')"><div class="icon gg-arrow-align-h" /></div>
                <div class="button" @click="adjustSplit('resize', 'up')"><div class="icon gg-push-right" style="margin-right: 4px;" /></div>
              </div>
            </div>
          </div>

          <component-footer v-if="layout.footer" />
        </div>
      </template>
    </transition>
  </div>
</template>

<script>
import Topbar from '@/components/Topbar.vue'
import Footer from '@/components/Footer.vue'
import Loader from '@/components/Loader.vue'
import EnvironmentBanner from '@/components/EnvironmentBanner.vue'

export default {
  name: 'ViewApp',

  components: {
    'component-topbar': Topbar,
    'component-footer': Footer,
    'component-loader': Loader,
    'component-environment-banner': EnvironmentBanner
  },

  data() {
    return {
      message: 'Loading...',
      notifications: {
        permission: null,
        handlers: {
          flex: false
        }
      },
      split: {
        enabled: false,
        visible: false,
        flex: false,
        layout: {
          side: 'right',
          size: 0
        }
      }
    }
  },

  computed: {
    layout() {
      const split = {
        reverse: false,
        size: null
      }

      if (this.split.layout.side === 'right') {
        split.reverse = false
      }

      if (this.split.layout.side === 'left') {
        split.reverse = true
      }

      split.size = this.split.layout.size * 100 + 402

      return {
        topbar: this.$route.meta.layout && this.$route.meta.layout.hasOwnProperty('topbar') ? this.$route.meta.layout.topbar : true,
        footer: this.$route.meta.layout && this.$route.meta.layout.hasOwnProperty('footer') ? this.$route.meta.layout.footer : true,
        split
      }
    },

    styles() {
      const styles = {}

      if (this.$store.getters['account/type'] === 'operator') {
        styles.width = `calc(100% - ${this.layout.split.size}px)`
      }

      if (!this.split.enabled) {
        styles.width = '100%'
      }

      return styles
    }
  },

  watch: {
    'split.flex': {
      deep: true,
      async handler(value) {
        if (value === true) {
          // Ask to enable notification if permission is not granted
          if (Notification.permission !== 'granted') {
            this.notifications.permission = await Notification.requestPermission()
          }
        }
      }
    },

    'split.enabled': {
      deep: true,
      handler(value) {
        const loader = this.$loader.show({ overlayOpacity: 1 })

        setTimeout(() => {
          this.split.visible = value
          loader.hide()
        }, 500)
      }
    },

    notifications: {
      deep: true,
      handler(value) {
        if (value.permission === 'granted') {
          window.addEventListener('message', this.handleMessage, false)
          this.notifications.handlers.flex = true
        }
      }
    }
  },

  async created() {
    this.notifications.permission = Notification.permission

    this.$store.watch(
      (state) => state.account.status.value,
      (status) => {
        if (this.$store.state.account.record && this.$store.state.account.profile && this.$route.name === 'login') {
          this.$router.push({ name: 'dashboard' })
        }

        if (status === true) {
          if (!this.$globals.iframe && this.$store.getters['account/type'] === 'operator') {
            this.split.enabled = true

            const splitCallCenterSize = localStorage.getItem('splitCallCenterSize') || false
            if (splitCallCenterSize !== false) {
              this.split.layout.size = splitCallCenterSize
            }

            const splitCallCenterSide = localStorage.getItem('splitCallCenterSide') || false
            if (splitCallCenterSide !== false) {
              this.split.layout.side = splitCallCenterSide
            }
          }

          if (this.$store.state.account.type === 'agent') {
            this.split.flex = true
          }
        } else {
          this.notifications = {
            permission: null,
            handlers: {
              flex: false
            }
          }

          this.split = {
            enabled: false,
            visible: false,
            flex: false,
            layout: {
              side: 'right',
              size: 0
            }
          }
        }
      }
    )

    this.$store.subscribe((mutation, state) => {
      if (mutation.type === 'setBoot') {
        this.message = state.boot
      }
    })
  },

  methods: {
    beforeEnter() {
      document.querySelector('#content').scrollTo(0, 0)
    },

    handleMessage(message) {
      if (message.data === 'reservationCreated') {
        // eslint-disable-next-line no-unused-vars, global-require
        const notification = new Notification('Contact Received', { body: 'Incoming Contact from Outer Space!', icon: require('@/assets/img/logo-icon-square-192.png') })
      }
    },

    adjustSplit(action, type = null) {
      if (action === 'resize') {
        let tempSize = this.split.layout.size

        if (type === 'down') {
          tempSize += 1
        }

        if (type === 'up') {
          tempSize -= 1
        }

        const vw = Math.max(document.documentElement.clientWidth, window.innerWidth || 0)
        const chunk = Math.round((vw - 802) / 100)
        const size = tempSize < chunk ? tempSize : chunk
        this.split.layout.size = size < 0 ? 0 : size
        localStorage.setItem('splitCallCenterSize', this.split.layout.size)
      }

      if (action === 'swap') {
        this.$refs.content.style.opacity = 0

        setTimeout(() => {
          let side = 'right'
          if (this.split.layout.side === 'right') {
            side = 'left'
          } else if (this.split.layout.side === 'left') {
            side = 'right'
          }

          this.split.layout.side = side
          localStorage.setItem('splitCallCenterSide', this.split.layout.side)

          this.$refs.content.style.opacity = 1
        }, 250)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.app {
  min-height: calc(100vh);
  background-color: $colour-background;

  .content {
    // flex: 1;
    box-sizing: border-box;
    height: calc(100vh - 120px);
    padding: 30px 0;
    overflow: auto;
    transition: opacity 250ms ease;

    &.split {
      display: flex;
      position: relative;
      flex-direction: row;
      height: calc(100vh - 120px);
      padding: 0;

      &.split-hidden {
        .flex {
          display: none;
        }

        .page {
          min-width: 100%;
          border-right: 0;
        }
      }

      .flex,
      .page {
        position: relative;
        min-width: 400px;
        transition: width 250ms ease;
      }

      .page {
        width: calc(100% - 402px);
        padding: 30px 0;
        overflow-x: auto;
        border-right: 2px solid #ccc;
      }

      .flex {
        flex: 1;

        .flex-frame,
        .flex-prompt {
          width: 100%;
          height: 100%;
        }

        .flex-prompt {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .controls {
          display: flex;
          position: absolute;
          bottom: 0;
          left: 0;
          padding: 20px 20px 0 20px;
          transform: translateX(calc(-50% - 1px));
          transition: opacity 250ms ease-in-out;
          opacity: .25;

          &:hover {
            z-index: 951; // Prevent Topbar shadow overlay
            opacity: 1;
          }

          .button {
            --ggs: .8;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            margin-right: 2px;
            transition-property: background;
            transition-duration: 250ms;
            transition-timing-function: ease-in-out;
            background: $colour-secondary;
            color: $colour-light;
            cursor: pointer;

            &:hover {
              background: $colour-primary;
            }

            &:last-of-type {
              margin: 0;
            }
          }
        }
      }

      &.split-reverse {
        flex-direction: row-reverse;

        .page {
          border-right: 0;
          border-left: 2px solid #ccc;
        }

        .flex .controls {
          left: 100%;
          transform: rotateY(180deg) translateX(calc(50% - 1px));
        }
      }
    }
  }

  // Specific Page Adjustments
  .login .content {
    height: calc(100vh - 40px);
    padding: 0;
  }
}
</style>
